import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { DEMO_ENABLED } from "@/lib/demo";
import { Sprout, ShieldCheck, KeyRound } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const Login = () => {
  const { t } = useLang();
  const [params] = useSearchParams();
  const error = params.get("error");
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [demoBusy, setDemoBusy] = useState(false);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleDemo = async () => {
    setDemoBusy(true);
    try {
      await api.post("/auth/demo-login");
      await refresh();
      navigate("/", { replace: true });
    } catch (e) {
      console.error("Demo login failed", e);
      setDemoBusy(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-[calc(100vh-64px)] flex items-center justify-center px-4 py-12 bg-pattern-dots">
      <div className="card-gc p-7 sm:p-9 w-full max-w-md">
        <div className="flex flex-col items-center mb-6">
          <div className="w-14 h-14 rounded-2xl bg-[#1E4D2B] text-white flex items-center justify-center mb-3">
            <Sprout size={26} />
          </div>
          <h1 className="text-2xl font-bold text-stone-900 text-center">{t.auth.login_title}</h1>
          <p className="text-stone-600 text-sm mt-1.5 text-center">{t.auth.login_sub}</p>
        </div>

        {error && (
          <div data-testid="login-error" className="mb-4 px-4 py-3 rounded-xl bg-red-50 text-red-700 text-sm border border-red-200">
            {t.auth.error_generic}
          </div>
        )}

        <button
          data-testid="google-signin-btn"
          onClick={handleGoogle}
          className="w-full h-12 rounded-xl border-2 border-stone-300 bg-white text-stone-800 font-semibold flex items-center justify-center gap-3 hover:border-[#1E4D2B] hover:bg-stone-50 transition-all active:scale-[0.99]"
        >
          {/* Google G icon */}
          <svg width="20" height="20" viewBox="0 0 48 48">
            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 8 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.8 1.1 8 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.6 8.4 6.3 14.7z"/>
            <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.5-4.6 2.4-7.2 2.4-5.2 0-9.6-3.3-11.3-8L6.2 33C9.5 39.6 16.2 44 24 44z"/>
            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.3-4.1 5.6l6.2 5.2c-.4.4 6.6-4.8 6.6-14.8 0-1.3-.1-2.4-.4-3.5z"/>
          </svg>
          <span>{t.auth.continue_google}</span>
        </button>

        <div className="mt-6 flex items-start gap-2 text-xs text-stone-500 leading-relaxed">
          <ShieldCheck size={16} className="flex-shrink-0 mt-0.5 text-[#1E4D2B]" />
          <span>{t.auth.privacy_note}</span>
        </div>

        {DEMO_ENABLED && (
        <div className="mt-6 pt-5 border-t border-dashed border-stone-300">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-stone-400 mb-2 text-center">
            Developer / Admin
          </p>
          <button
            data-testid="demo-login-btn"
            onClick={handleDemo}
            disabled={demoBusy}
            className="w-full h-11 rounded-xl bg-stone-100 hover:bg-stone-200 border border-stone-300 text-stone-700 text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
          >
            <KeyRound size={15} />
            {demoBusy ? t.common.loading : "Demo Login (Skip OAuth)"}
          </button>
          <p className="mt-2 text-[10px] text-center text-stone-400">
            Bypass Google auth for internal testing · <code>?demo=true</code>
          </p>
        </div>
        )}

        <button
          onClick={() => navigate("/")}
          data-testid="back-home-link"
          className="mt-5 w-full text-sm text-stone-500 hover:text-stone-700"
        >
          ← {t.common.back}
        </button>
      </div>
    </div>
  );
};

export default Login;
